import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";
import { clearPendingCheckout, cartAfterPaidCheckout } from '../services/checkoutService';
import { useCart } from '../context/CartContext';
import { useQueryClient } from '@tanstack/react-query';
import useCustomerCollections from '../hooks/useCustomerCollections';

function PaymentSuccessPage() {
  const queryClient = useQueryClient();
  const { items, dispatch } = useCart();
  const collections = useCustomerCollections();
  const cart = useRef(items); cart.current = items;
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const attemptId = params.get("attemptId");
  const [state, setState] = useState({ status: "PENDING" });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false,
      timer,
      count = 0;
    setState({ status: "PENDING" });
    async function check() {
      try {
        if (
          !/^[1-9]\d*$/.test(orderId || "") ||
          (attemptId && !/^[1-9]\d*$/.test(attemptId))
        )
          throw new Error("invalid reference");
        let data;
        if (attemptId) data = (await api.get(`/api/payment/attempts/${attemptId}/status`)).data;
        else {
          try { data = (await api.get(`/api/payment/orders/${orderId}/status`)).data; }
          catch (error) {
            if (error.response?.status !== 503) throw error;
            const response = await api.get(`/api/orders/customer/${orderId}`);
            data = { status: response.data.status === 'PAID' ? 'SUCCEEDED' : 'PENDING' };
          }
        }
        if (attemptId && String(data.orderId) !== orderId)
          throw new Error("reference mismatch");
        if (cancelled) return;
        setState(data);
        if (['SUCCEEDED', 'REFUNDED', 'CANCELLED'].includes(data.status)) {
          if (data.status !== 'SUCCEEDED') clearPendingCheckout(orderId);
          queryClient.invalidateQueries({ queryKey: ['customer-orders'] });
        }
        if (
          !['SUCCEEDED', 'FAILED', 'AUTHORIZED', 'REFUNDED', 'CANCELLED', 'UNPAID'].includes(data.status) &&
          ++count < 30
        )
          timer = setTimeout(check, 2000);
      } catch (error) {
        if (!cancelled)
          setState({
            status: "UNKNOWN",
            login: error.response?.status === 401,
          });
      }
    }
    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId, attemptId, retry, queryClient, dispatch]);
  useEffect(() => {
    if (state.status !== 'SUCCEEDED' || !collections.verified) return undefined;
    let active = true;
    cartAfterPaidCheckout(orderId, cart.current).then(next => {
      if (!active) return;
      if (next) dispatch({ type: 'LOAD_CART', payload: next });
      clearPendingCheckout(orderId);
    }).catch(() => { /* Preserve the continuation for retry; the payment result is independent. */ });
    return () => { active = false; };
  }, [state.status, collections.verified, orderId, dispatch]);
  const paid = state.status === "SUCCEEDED";
  const message = paid
    ? "Оплата подтверждена банком. Статус заказа доступен в личном кабинете."
    : state.status === 'REFUNDED' ? 'Банк подтвердил полный возврат оплаты.'
    : state.status === 'REFUND_PENDING' ? 'Возврат обрабатывается. Дождитесь подтверждения банка.'
    : state.status === 'CANCELLED' ? 'Заказ отменён.'
    : state.status === 'UNPAID' ? 'Заказ ещё не оплачен. Откройте его, чтобы перейти к оплате.'
    : state.status === "FAILED"
      ? "Банк не подтвердил оплату. Деньги за этот заказ не считаются полученными."
      : state.status === "AUTHORIZED"
        ? "Банк зарезервировал сумму. Окончательное списание ещё не подтверждено."
        : state.login
          ? "Войдите в аккаунт покупателя, чтобы проверить оплату."
          : "Оплата пока не подтверждена. Проверяем статус в банке. Не оплачивайте заказ повторно до проверки.";
  return (
    <div className="orders-page orders-empty">
      <h1>
        {paid
          ? "Оплачено"
          : state.status === 'REFUNDED' ? 'Оплата возвращена'
          : state.status === "FAILED"
            ? "Не получилось оплатить"
            : "Статус оплаты"}
      </h1>
      <p role="status" aria-live="polite">
        {message}
      </p>
      {state.testMode && (
        <p>Тестовая оплата. Реальная доставка не создаётся.</p>
      )}
      <div className="success-actions">
        {!paid && (
          <button
            className="order-primary-button"
            onClick={() => setRetry((value) => value + 1)}
          >
            Проверить статус
          </button>
        )}
        {state.login && (
          <Link className="order-secondary-button" to="/profile" state={{ from: `/checkout/success?${params.toString()}` }}>
            Войти
          </Link>
        )}
        {orderId && (
          <Link className="order-primary-button" to={`/orders/${orderId}`}>
            Открыть заказ
          </Link>
        )}
        <Link className="order-secondary-button" to="/orders">
          Мои заказы
        </Link>
      </div>
    </div>
  );
}
export default PaymentSuccessPage;
